import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getFirestore, collection, query, where, getDocs, setDoc, doc } from 'firebase/firestore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Helper function to format date as dd-MMM-yyyy
const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' }); // Short month name (e.g., Jan, Feb)
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const AttendanceChart = ({ selectedOption, user }) => {
  const [dailyHours, setDailyHours] = useState([]);
  const [monthlyHours, setMonthlyHours] = useState([]);
  const [timePeriodLabel, setTimePeriodLabel] = useState('');
  const [weeklyHours, setWeeklyHours] = useState([]);


  useEffect(() => {
    const fetchDailyHours = async (startDate, endDate) => {
      const db = getFirestore();
      const tasksTimesheetRef = collection(db, 'tasksTimesheet');
      const q = query(
        tasksTimesheetRef,
        where('employeeId', '==', user.id),
        where('date', '>=', startDate.toISOString().split('T')[0]),
        where('date', '<', endDate.toISOString().split('T')[0])
      );
      const querySnapshot = await getDocs(q);

      const hoursMap = {};

      querySnapshot.forEach(doc => {
        const data = doc.data();
        const date = data.date;
        const startTime = new Date(`${date}T${data.startTime}`);
        const endTime = new Date(`${date}T${data.endTime}`);
        const hoursWorked = (endTime - startTime) / (1000 * 60 * 60);

        if (hoursMap[date]) {
          hoursMap[date] += hoursWorked;
        } else {
          hoursMap[date] = hoursWorked;
        }
      });

      return Object.keys(hoursMap).map(date => ({
        date: formatDate(new Date(date)),
        hours: hoursMap[date],
      }));
    };

    const fetchMonthlyHours = async () => {
      const db = getFirestore();
      const employeeMonthlyHoursRef = collection(db, 'employeeMonthlyHours');
      const q = query(
        employeeMonthlyHoursRef,
        where('employeeId', '==', user.id)
      );
      const querySnapshot = await getDocs(q);

      const monthlyData = [];

      querySnapshot.forEach(doc => {
        const data = doc.data();
        const month = data.month;
        const monthlyHoursString = data.monthlyHours;
        const hours = parseInt(monthlyHoursString.split(' ')[0], 10); // Extract hours
        const minutes = parseInt(monthlyHoursString.split(' ')[2], 10); // Extract minutes
        const totalHours = hours + minutes / 60;

        monthlyData.push({
          month,
          monthlyHours: totalHours,
        });
      });

      return monthlyData;
    };

    const fetchWeeklyHours = async (startDate, endDate, user) => {
      const db = getFirestore();
      const tasksTimesheetRef = collection(db, 'tasksTimesheet');
      const q = query(
        tasksTimesheetRef,
        where('employeeId', '==', user.id),
        where('date', '>=', startDate.toISOString().split('T')[0]),
        where('date', '<', endDate.toISOString().split('T')[0])
      );
      const querySnapshot = await getDocs(q);
    
      const weeklyMap = {};
    
      // Retrieve the employee's joining date
      const joiningDate = new Date(user.joiningDate);
    
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const date = new Date(data.date);
        const startTime = new Date(`${data.date}T${data.startTime}`);
        const endTime = new Date(`${data.date}T${data.endTime}`);
        const hoursWorked = (endTime - startTime) / (1000 * 60 * 60);
    
        // Calculate custom month start date based on employee's joining day
        const monthStart = new Date(date.getFullYear(), date.getMonth(), joiningDate.getDate());
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthStart.getMonth() + 1); // Set end to the next month, same day
        monthEnd.setDate(joiningDate.getDate() - 1);  // Set the end date (one day before)
    
        // Adjust if the current date is before the joining date, shift to the previous month
        if (date < monthStart) {
          monthStart.setMonth(monthStart.getMonth() - 1);
          monthEnd.setMonth(monthStart.getMonth());
        }
    
        // Calculate how many days have passed since the start of the month
        const daysSinceMonthStart = Math.floor((date - monthStart) / (1000 * 60 * 60 * 24));
    
        // Calculate the start of the week by finding the first day of this week (daysSinceMonthStart rounded down to nearest multiple of 7)
        const weekStart = new Date(monthStart);
        weekStart.setDate(monthStart.getDate() + 1 + Math.floor(daysSinceMonthStart / 7) * 7);
    
        // Calculate the end of the week by adding 6 days to the start of the week
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
    
        // Format both the start and end dates as dd-mm-yyyy
        const formatDate = (d) => d.toISOString().split('T')[0].split('-').reverse().join('-');
        const weekLabel = `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
    
        // Aggregate the hours worked in weeklyMap
        if (weeklyMap[weekLabel]) {
          weeklyMap[weekLabel] += hoursWorked;
        } else {
          weeklyMap[weekLabel] = hoursWorked;
        }
      });
    
      const weeklyData = Object.keys(weeklyMap).map(week => ({
        week,  // This is now the start and end date in the format "dd-mm-yyyy - dd-mm-yyyy"
        weeklyHours: weeklyMap[week],
      }));
    
      // Save the calculated weekly hours to the database with custom document ID
      weeklyData.forEach(async weekData => {
        const docId = `${user.id}-${weekData.week}`;  // Use start and end date for the document ID
        const weeklyAttendanceRef = doc(db, 'weeklyAttendance', docId);
        await setDoc(weeklyAttendanceRef, {
          employeeId: user.id,
          weeklyHours: weekData.weeklyHours,
          week: weekData.week,  // The start and end date as the week field
          timestamp: new Date().toISOString(),
        });
      });
    
      return weeklyData;
    };
    

    const calculateAttendance = async () => {
      if (!user || !user.id || !user.joiningDate) return;
    
      const joiningDate = new Date(user.joiningDate);
      const currentMonthDate = new Date();
      const paydayDate = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth(), joiningDate.getDate());
    
      let startOfMonth, endOfMonth;
    
      if (currentMonthDate >= paydayDate) {
        // Current date is after or on the payday date, so show next month's attendance
        startOfMonth = paydayDate;
        endOfMonth = new Date(paydayDate.getFullYear(), paydayDate.getMonth() + 1, paydayDate.getDate() - 1);
      } else {
        // Current date is before the payday date, so show current month's attendance
        startOfMonth = new Date(paydayDate.getFullYear(), paydayDate.getMonth() - 1, paydayDate.getDate());
        endOfMonth = new Date(paydayDate.getFullYear(), paydayDate.getMonth(), paydayDate.getDate() - 1);
      }
    
      // Set the time period label
      setTimePeriodLabel(`${formatDate(startOfMonth)} to ${formatDate(endOfMonth)}`);
    
      if (selectedOption === 'daily') {
        const dailyData = await fetchDailyHours(startOfMonth, endOfMonth);
        setDailyHours(dailyData);
      } else if (selectedOption === 'monthly') {
        const monthlyData = await fetchMonthlyHours();
        setMonthlyHours(monthlyData);
      } else if (selectedOption === 'weekly') {
        const weeklyData = await fetchWeeklyHours(startOfMonth, endOfMonth, user);
        setWeeklyHours(weeklyData);
      }
    };
    

    calculateAttendance();
  }, [user, selectedOption]);

  return (
    <Box sx={{ height: 350, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {selectedOption === 'daily' ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dailyHours} margin={{ top: 20, right: 20, bottom: 25, left: 0 }}>
            <CartesianGrid stroke="#ccc" />
            <XAxis dataKey="date" label={{ value: timePeriodLabel, position: 'insideBottom', offset: -20 }} />
            <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft', offset: 10 }} />
            <Tooltip />
            <Bar dataKey="hours" fill="#39B54A" />
          </BarChart>
        </ResponsiveContainer>
      ) : selectedOption === 'monthly' ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={monthlyHours} margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#ccc" />
            <XAxis dataKey="month" label={{ value: 'Month', position: 'insideBottom', offset: -20 }} />
            <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft', offset: 10 }} />
            <Tooltip />
            <Bar dataKey="monthlyHours" fill="#39B54A" />
          </BarChart>
        </ResponsiveContainer>
      ) : selectedOption === 'weekly' ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weeklyHours} margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#ccc" />
            <XAxis dataKey="week" label={{ value: 'Week', position: 'insideBottom', offset: -20 }} />
            <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft', offset: 10 }} />
            <Tooltip />
            <Bar dataKey="weeklyHours" fill="#39B54A" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="h6">
          {selectedOption.charAt(0).toUpperCase() + selectedOption.slice(1)} Attendance Chart Placeholder
        </Typography>
      )}
    </Box>
  );
  
};

export default AttendanceChart;
